import { AbstractControl, ValidatorFn } from '@angular/forms';
import moment from 'moment';

/**
 * RPD-20: the `hide_dates_in_future` / `hide_dates_in_past` schema flags were
 * only wired to the native `max` attribute on the date input. That constrains
 * the date picker, but the reactive form never sees it — so a date typed in
 * with the keyboard was accepted and the journey continued with a bad answer.
 *
 * These validators put the same rule on the control itself, so every edit
 * (picker, keyboard, paste, autofill) is revalidated.
 */

/** Date input values are always ISO yyyy-mm-dd, per the native date control. */
const CONTROL_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Returns the date only when the value is a complete ISO date.
 *
 * Anything else — empty, or half-typed — is deliberately not this validator's
 * concern: `required` decides whether empty is allowed, and the my-ceremonies
 * journeys assemble their control value from separate day/month/year boxes, so
 * mid-typing they legitimately hold fragments like `2026-01-`. Parsing is
 * strict so those fragments are ignored rather than reported as errors, which
 * would otherwise block an optional part-filled date that used to be accepted.
 */
function completeDate(value: unknown): moment.Moment | null {
	if (typeof value !== 'string' || value === '') {
		return null;
	}
	const date = moment(value, CONTROL_DATE_FORMAT, true).startOf('day');
	return date.isValid() ? date : null;
}

export function noFutureDate(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const date = completeDate(control.value);
		if (date && date.isAfter(moment().startOf('day'))) {
			return {
				hideDatesInFuture: {
					value: control.value,
					message: 'Date cannot be in the future',
				},
			};
		}
		return null;
	};
}

export function noPastDate(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const date = completeDate(control.value);
		if (date && date.isBefore(moment().startOf('day'))) {
			return {
				hideDatesInPast: {
					value: control.value,
					message: 'Date cannot be in the past',
				},
			};
		}
		return null;
	};
}
